/* eslint-disable no-unused-vars */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebaseFunctions/config';

export const invitedVendorsService = {
  async addInvitedVendor(eoUserId, eventId, vendorData) {
    try {
      const vendorsRef = collection(db, 'users', eoUserId, 'invitedVendors');
      
      const existingSnapshot = await getDocs(vendorsRef);
      const existingVendor = existingSnapshot.docs.find(
        doc => doc.data().businessEmail?.toLowerCase() === vendorData.businessEmail?.toLowerCase()
      );
      
      if (existingVendor) {
        return { success: false, message: 'This vendor has already been invited' };
      }

      const newVendor = {
        eventId: eventId || null,
        businessName: vendorData.businessName,
        businessEmail: vendorData.businessEmail,
        businessPhone: vendorData.businessPhone || '',
        businessType: vendorData.businessType || '',
        status: 'Invited',
        paymentStatus: 'Not Started',
        invitedAt: serverTimestamp(),
        lastNotificationSent: null,
        notificationCount: 0,
        hasSignedUp: false,
        mpsUserId: null
      };

      const docRef = await addDoc(vendorsRef, newVendor);
      return { success: true, vendorId: docRef.id, vendor: { id: docRef.id, ...newVendor, invitedAt: new Date() } };
    } catch (error) {
      console.error('Error adding invited vendor:', error);
      return { success: false, message: error.message };
    }
  },

  async getInvitedVendors(eoUserId, eventId = null) {
    try {
      const vendorsRef = collection(db, 'users', eoUserId, 'invitedVendors');
      const snapshot = await getDocs(vendorsRef);
      
      let vendors = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        invitedAt: doc.data().invitedAt?.toDate?.() || new Date()
      }));

      if (eventId) {
        vendors = vendors.filter(v => v.eventId === eventId);
      }

      vendors.sort((a, b) => new Date(b.invitedAt) - new Date(a.invitedAt));

      return { success: true, vendors };
    } catch (error) {
      console.error('Error fetching invited vendors:', error);
      return { success: false, message: error.message, vendors: [] };
    }
  },

  async updateInvitedVendor(eoUserId, vendorId, updates) {
    try {
      const vendorRef = doc(db, 'users', eoUserId, 'invitedVendors', vendorId);
      await updateDoc(vendorRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating invited vendor:', error);
      return { success: false, message: error.message };
    }
  },

  async deleteInvitedVendor(eoUserId, vendorId) {
    try {
      const vendorRef = doc(db, 'users', eoUserId, 'invitedVendors', vendorId);
      await deleteDoc(vendorRef);
      return { success: true };
    } catch (error) {
      console.error('Error deleting invited vendor:', error);
      return { success: false, message: error.message };
    }
  },

  async markAsSignedUp(eoUserId, vendorId, mpsUserId) {
    try {
      const vendorRef = doc(db, 'users', eoUserId, 'invitedVendors', vendorId);
      await updateDoc(vendorRef, {
        hasSignedUp: true,
        mpsUserId,
        status: 'Pending',
        signedUpAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error marking vendor as signed up:', error);
      return { success: false, message: error.message };
    }
  },

  async recordNotificationSent(eoUserId, vendorId, notificationType) {
    try {
      const vendorRef = doc(db, 'users', eoUserId, 'invitedVendors', vendorId);
      const vendorSnap = await getDoc(vendorRef);
      
      if (!vendorSnap.exists()) {
        return { success: false, message: 'Vendor not found' };
      }

      const currentCount = vendorSnap.data().notificationCount || 0;
      
      await updateDoc(vendorRef, {
        lastNotificationSent: serverTimestamp(),
        lastNotificationType: notificationType,
        notificationCount: currentCount + 1
      });
      return { success: true };
    } catch (error) {
      console.error('Error recording notification:', error);
      return { success: false, message: error.message };
    }
  },

  async sendInviteNotification(eoUserId, vendorId) {
    try {
      const vendorRef = doc(db, 'users', eoUserId, 'invitedVendors', vendorId);
      const vendorSnap = await getDoc(vendorRef);
      
      if (!vendorSnap.exists()) {
        return { success: false, message: 'Vendor not found' };
      }

      const vendor = vendorSnap.data();
      
      await this.recordNotificationSent(eoUserId, vendorId, 'signup_invite');
      
      return { 
        success: true, 
        message: `Invitation sent to ${vendor.businessEmail}`,
        vendor: { id: vendorId, ...vendor }
      };
    } catch (error) {
      console.error('Error sending invite notification:', error);
      return { success: false, message: error.message };
    }
  },

  async sendPaymentReminder(eoUserId, vendorId) {
    try {
      const vendorRef = doc(db, 'users', eoUserId, 'invitedVendors', vendorId);
      const vendorSnap = await getDoc(vendorRef);
      
      if (!vendorSnap.exists()) {
        return { success: false, message: 'Vendor not found' };
      }

      const vendor = vendorSnap.data();
      
      await this.recordNotificationSent(eoUserId, vendorId, 'payment_reminder');
      
      return { 
        success: true, 
        message: `Payment reminder sent to ${vendor.businessEmail}`,
        vendor: { id: vendorId, ...vendor }
      };
    } catch (error) {
      console.error('Error sending payment reminder:', error);
      return { success: false, message: error.message };
    }
  }
};
